import type {EmptyStateProps} from 'shells/card/card-widget/card-widget';
import type {TabProps} from 'shells/control-bar/page-control-bar';
import {EnvelopesRegularIcon} from 'shells/icon/svg/envelopes-regular-icon';
import {getHumanReadableTimespan} from 'shells/row/drip-sequence-row/helpers';
import type {PersonalEmailSequenceToView} from 'shells/row/personal-email-sequence-row/types';

import type {EmailAutomationStatusesFragment, EmailSequenceFragment} from 'nutshell-graphql-types';

import type {
    ContactWithDripProgressions,
    FlattenedDripSequence,
    PersonalEmailSequenceToViewSparse,
    EmailAutomationStatusesSeparated,
    ManuallyStartedEmailSequencesSeparated,
    QueuedEmailWithSequence,
} from './types';

export function getEmptyStateProps(
    flattenedDripSequencesCount: number,
    activeEmailAutomationStatusesCount: number,
    completeEmailAutomationStatusesCount: number,
    manuallyStartedEmailSequencesCount: number,
    completeManuallyStartedEmailSequencesCount: number,
    selectedTab: string
): EmptyStateProps | undefined {
    const activeCount =
        flattenedDripSequencesCount +
        activeEmailAutomationStatusesCount +
        manuallyStartedEmailSequencesCount;
    const completeCount =
        completeEmailAutomationStatusesCount + completeManuallyStartedEmailSequencesCount;

    if (selectedTab === 'active') {
        return {
            isEmpty: activeCount === 0,
            text: 'No active sequences',
            icon: EnvelopesRegularIcon,
            iconSize: 22,
        };
    } else if (selectedTab === 'completed') {
        return {
            isEmpty: completeCount === 0,
            text: 'No completed sequences',
            icon: EnvelopesRegularIcon,
            iconSize: 22,
        };
    }
}

export function getTabProps(
    activeCount: number,
    completeCount: number,
    selectedTab: string,
    setSelectedTab: (tab: string) => void
): TabProps {
    const options = [
        {
            id: 'active',
            label: 'Active',
            count: String(activeCount),
        },
        {
            id: 'completed',
            label: 'Completed',
            count: String(completeCount),
        },
    ];

    return {
        options: options,
        selectedTab: selectedTab,
        onChangeTab: (tab: string) => {
            setSelectedTab(tab);
        },
    };
}

export const getEmailSequenceStartTrigger = (
    emailAutomationStatus: EmailAutomationStatusesFragment
): string => {
    if (
        emailAutomationStatus.emailAutomation.delay &&
        emailAutomationStatus.emailAutomation.delay.unit
    ) {
        return `Sending ${getHumanReadableTimespan(
            emailAutomationStatus.emailAutomation.delay.timespan,
            emailAutomationStatus.emailAutomation.delay.unit
        )} after the lead enters ${emailAutomationStatus.emailAutomation.entersStage.name}`;
    }

    return `Sending after the lead enters ${emailAutomationStatus.emailAutomation.entersStage.name}`;
};

export const getEmailSequenceTitle = (emailAutomationStatus: EmailAutomationStatusesFragment) => {
    return emailAutomationStatus.emailAutomation.emailList &&
        emailAutomationStatus.emailAutomation.emailList.name
        ? emailAutomationStatus.emailAutomation.emailList.name
        : 'Email sequence';
};

export const getEmailSequenceStopSendCondition = (
    emailAutomationStatus: EmailAutomationStatusesFragment
) => {
    if (emailAutomationStatus.emailAutomation.exitsStage) {
        return `If no reply, stop when the lead exits ${emailAutomationStatus.emailAutomation.exitsStage.name}`;
    }

    return 'If no reply, stop when the lead closes';
};

const TEMPLATE_FIELDNAMES = ['firstName', 'lastName', 'companyName'];

export function getAutomationProblemDescription(
    emailAutomation: EmailAutomationStatusesFragment
): string | null | undefined {
    if (isStatusCancelled(emailAutomation)) {
        const completedSequenceMessageCount =
            emailAutomation.emailSequence && emailAutomation.emailSequence.emailsQueued.length;

        if (completedSequenceMessageCount) {
            return `Cancelled after ${completedSequenceMessageCount} ${
                completedSequenceMessageCount > 1 ? 'emails' : 'email'
            } sent`;
        } else {
            return `Cancelled before any emails were sent`;
        }
    }

    if (emailAutomation.isPaused) {
        return `When sending is turned back on, this email sequence will be automatically rescheduled`;
    }

    const primaryContact =
        emailAutomation.lead.contacts.primaryEdge && emailAutomation.lead.contacts.primaryEdge.node;
    const primaryContactName = (primaryContact && primaryContact.name) || 'the related person';

    if (!primaryContact) {
        return `Add a related person`;
    }

    if (!primaryContact.emailAddresses.primary) {
        return `Add an email address for ${primaryContactName}`;
    }

    const missingFields = TEMPLATE_FIELDNAMES.filter(
        // @ts-expect-error
        (fieldName) => emailAutomation[fieldName].required && !emailAutomation[fieldName].value
    );

    if (missingFields.length) {
        const isMissingFirst = missingFields.includes('firstName');
        const isMissingLast = missingFields.includes('lastName');
        const isMissingCompany = missingFields.includes('companyName');

        if (isMissingFirst && isMissingLast) {
            return `Add first and last names for ${primaryContactName}`;
        } else if (isMissingFirst) {
            return `Add a first name for ${primaryContactName}`;
        } else if (isMissingLast) {
            return `Add a last name for ${primaryContactName}`;
        }

        if (isMissingCompany) {
            return `Add a company to ${primaryContactName}`;
        }
    }

    if (
        emailAutomation.sender &&
        !emailAutomation.sender.sendableEmailAddresses.filter(
            (emailAddresses) => emailAddresses.isEnabledForSending
        ).length
    ) {
        return 'This email sequence has not sent because the sender is not configured';
    }

    if (
        emailAutomation.status === 'READY_TO_SEND' ||
        emailAutomation.status === 'NEEDS_ATTENTION'
    ) {
        return `This email sequence was not sent automatically`;
    }

    if (emailAutomation.status === 'PREEXISTING_LEAD_EXEMPTED') {
        return `This email sequence was not sent automatically because the lead existed before setting up this automated email`;
    }

    if (emailAutomation.status === 'PREEXISTING_SEQUENCE_EXEMPTED') {
        return `${primaryContactName} is already receiving this email sequence`;
    }

    if (!emailAutomation.lead.assignee) {
        return 'This email sequence has not sent because the lead is not assigned';
    }

    if (emailAutomation.lead.assignee && emailAutomation.lead.assignee.__typename === 'Team') {
        return 'This email sequence has not sent because the lead is assigned to a team';
    }

    return null;
}

export function getAutomationHasProblem(emailAutomation: EmailAutomationStatusesFragment): boolean {
    if (!emailAutomation.emailSequence || isStatusCancelled(emailAutomation)) {
        return true;
    }

    return false;
}

function isStatusCancelled(emailAutomation: EmailAutomationStatusesFragment) {
    return (
        emailAutomation.status === 'CANCELLED_MANUALLY' ||
        emailAutomation.status === 'CANCELLED_AUTOMATICALLY'
    );
}

export function getCurrentPersonalEmailSequenceToView(
    manuallyStartedEmailSequences: ManuallyStartedEmailSequencesSeparated,
    emailAutomationStatuses: EmailAutomationStatusesSeparated,
    personalEmailSequenceToView: PersonalEmailSequenceToViewSparse
): PersonalEmailSequenceToView | null | undefined {
    if (personalEmailSequenceToView.type === 'manually-started') {
        let emailSequence = manuallyStartedEmailSequences.activeSequences.find(
            (sequence) => sequence.id === personalEmailSequenceToView.id
        );

        if (!emailSequence) {
            emailSequence = manuallyStartedEmailSequences.finishedSequences.find(
                (sequence) => sequence.id === personalEmailSequenceToView.id
            );
        }

        if (!emailSequence) {
            return null;
        }

        const title =
            emailSequence.emailSequenceTemplate && emailSequence.emailSequenceTemplate.name;

        return {
            type: 'manually-started',
            id: emailSequence.id,
            title,
            startTrigger: 'Started manually',
            canCancel: Boolean(emailSequence.nextUnsentEmail),
            canStart: false,
            sendFromUser:
                // @ts-expect-error
                emailSequence.emailsQueued.length > 0 && emailSequence.emailsQueued[0].fromUser
                    ? {
                          // @ts-expect-error
                          id: emailSequence.emailsQueued[0].fromUser.id,
                          // @ts-expect-error
                          name: emailSequence.emailsQueued[0].fromUser.name,
                      }
                    : null,
            emails: emailSequence.emailsQueued,
            recipientNames:
                emailSequence.emailsQueued.length > 0
                    ? // @ts-expect-error
                      emailSequence.emailsQueued[0].to.map((to) =>
                          to.avatarEntity ? to.avatarEntity.name : null
                      )
                    : null,
            isCompleted: emailSequence.nextUnsentEmail === null,
            messageCount: emailSequence.messageCount,
            sentEmailsCount: emailSequence.sentEmailsCount,
        };
    } else if (personalEmailSequenceToView.type === 'stage') {
        let automationStatus =
            emailAutomationStatuses.activeStatuses &&
            emailAutomationStatuses.activeStatuses.find(
                (status) => status.id === personalEmailSequenceToView.id
            );

        if (!automationStatus) {
            automationStatus =
                emailAutomationStatuses.finishedStatuses &&
                emailAutomationStatuses.finishedStatuses.find(
                    (status) => status.id === personalEmailSequenceToView.id
                );
        }

        if (!automationStatus) {
            return null;
        }

        const startTrigger = getEmailSequenceStartTrigger(automationStatus);
        const title = getEmailSequenceTitle(automationStatus);
        const problemDescription = getAutomationProblemDescription(automationStatus);
        const stopSendCondition = getEmailSequenceStopSendCondition(automationStatus);
        const hasProblem = getAutomationHasProblem(automationStatus);

        const emailSequence = automationStatus.emailSequence;

        return {
            type: 'stage',
            id: automationStatus.id,
            title,
            startTrigger,
            stopSendCondition,
            canCancel: automationStatus.canCancel,
            canStart: automationStatus.canStart,
            sendFromUser: automationStatus.sender
                ? {
                      id: automationStatus.sender.id,
                      name: automationStatus.sender.name,
                  }
                : null,
            emails: emailSequence ? emailSequence.emailsQueued : [],
            recipientNames:
                emailSequence && emailSequence.emailsQueued.length > 0
                    ? // @ts-expect-error
                      emailSequence.emailsQueued[0].to.map((to) =>
                          to.avatarEntity ? to.avatarEntity.name : null
                      )
                    : null,
            isCompleted: Boolean(emailSequence && emailSequence.nextUnsentEmail === null),
            hasProblem,
            problemDescription,
            messageCount: emailSequence ? emailSequence.messageCount : 0,
            sentEmailsCount: emailSequence ? emailSequence.sentEmailsCount : 0,
        };
    }

    return null;
}

function getNextSendTime(
    sequence: FlattenedDripSequence | EmailSequenceFragment | EmailAutomationStatusesFragment
): number | null | undefined {
    if (sequence.__typename === 'DripProgression' && sequence.nextEditionSendTime) {
        return sequence.nextEditionSendTime;
    }

    if (
        sequence.__typename === 'EmailSequence' &&
        sequence.nextUnsentEmail &&
        sequence.nextUnsentEmail.deliveryTime
    ) {
        return sequence.nextUnsentEmail.deliveryTime;
    }

    if (
        sequence.__typename === 'EmailAutomationStatus' &&
        sequence.emailSequence &&
        sequence.emailSequence.nextUnsentEmail &&
        sequence.emailSequence.nextUnsentEmail.deliveryTime
    ) {
        return sequence.emailSequence.nextUnsentEmail.deliveryTime;
    }

    return null;
}

export function getFinalSendTime(
    sequence: FlattenedDripSequence | EmailSequenceFragment | EmailAutomationStatusesFragment
): number | null | undefined {
    if (sequence.__typename === 'EmailSequence' && sequence && sequence.emailsQueued.length > 0) {
        // @ts-expect-error
        return sequence.emailsQueued[sequence.emailsQueued.length - 1].deliveryTime;
    }

    if (
        sequence.__typename === 'EmailAutomationStatus' &&
        sequence.emailSequence &&
        sequence.emailSequence.emailsQueued.length > 0
    ) {
        // @ts-expect-error
        return sequence.emailSequence.emailsQueued[sequence.emailSequence.emailsQueued.length - 1]
            .deliveryTime;
    }

    return null;
}

const EXEMPTED_STATUSES = new Set(['PREEXISTING_SEQUENCE_EXEMPTED', 'PREEXISTING_LEAD_EXEMPTED']);

/**
 * Filters out exempted automation statuses that were blocked before any emails
 * were sent (e.g. contact already receiving the sequence from another lead, or
 * lead existed before automation was configured). On peep pages these are
 * noise, the actual running sequence surfaces through queuedEmails.
 */
export function filterExemptedAutomationStatuses(
    statuses: EmailAutomationStatusesFragment[]
): EmailAutomationStatusesFragment[] {
    return statuses.filter((status) => !EXEMPTED_STATUSES.has(status.status));
}

export function separateEmailAutomationStatuses(
    statuses: EmailAutomationStatusesFragment[]
): EmailAutomationStatusesSeparated {
    const activeStatuses: EmailAutomationStatusesFragment[] = [];
    const finishedStatuses: EmailAutomationStatusesFragment[] = [];

    statuses.forEach((status) => {
        if (
            (status.emailSequence && status.emailSequence.nextUnsentEmail !== null) ||
            status.status === 'NEEDS_ATTENTION' ||
            status.status === 'READY_TO_SEND' ||
            status.status === 'PREEXISTING_LEAD_EXEMPTED' ||
            status.status === 'PREEXISTING_SEQUENCE_EXEMPTED'
        ) {
            activeStatuses.push(status);
        } else {
            finishedStatuses.push(status);
        }
    });

    return {activeStatuses, finishedStatuses};
}

/**
 * Takes in array of contacts and their drip progressions and returns a list of
 * flattened drip sequences where the contact name and id are on every sequence.
 */
export function flattenDripProgressions(
    contacts: ContactWithDripProgressions[]
): FlattenedDripSequence[] {
    const sequences: FlattenedDripSequence[] = [];

    contacts.forEach((contact) => {
        if (contact.dripProgressions) {
            contact.dripProgressions.forEach((sequence) => {
                sequences.push({
                    ...sequence,
                    contactId: contact.id,
                    contactName: contact.name,
                });
            });
        }
    });

    return sequences;
}

export function getManuallyStartedEmailSequences(
    queuedEmails: QueuedEmailWithSequence[] | null | undefined,
    emailAutomationStatuses: EmailAutomationStatusesFragment[]
): ManuallyStartedEmailSequencesSeparated {
    if (!queuedEmails || !queuedEmails.length) return {activeSequences: [], finishedSequences: []};

    const alreadyAddedSequenceIds = new Set<string>();
    emailAutomationStatuses.forEach((emailAutomationStatus) => {
        if (emailAutomationStatus.emailSequence) {
            alreadyAddedSequenceIds.add(emailAutomationStatus.emailSequence.id);
        }
    });

    const emailsBySequenceId: Record<string, QueuedEmailWithSequence[]> = {};
    queuedEmails.forEach((queuedEmail) => {
        if (queuedEmail.emailSequence) {
            const sequenceId = queuedEmail.emailSequence.id;
            if (!emailsBySequenceId[sequenceId]) {
                emailsBySequenceId[sequenceId] = [];
            }
            emailsBySequenceId[sequenceId].push(queuedEmail);
        }
    });

    const activeSequences: EmailSequenceFragment[] = [];
    const finishedSequences: EmailSequenceFragment[] = [];
    Object.keys(emailsBySequenceId).forEach((sequenceId) => {
        if (!alreadyAddedSequenceIds.has(sequenceId)) {
            alreadyAddedSequenceIds.add(sequenceId);
            const emails = emailsBySequenceId[sequenceId];
            const sequence = emails?.[0]?.emailSequence;

            if (sequence) {
                const sequenceWithEmails: EmailSequenceFragment = {
                    ...sequence,
                    emailsQueued: emails,
                };

                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                sequenceWithEmails.nextUnsentEmail
                    ? activeSequences.push(sequenceWithEmails)
                    : finishedSequences.push(sequenceWithEmails);
            }
        }
    });

    return {activeSequences, finishedSequences};
}

// Sorts sequences by either next send time or final send time depending on
// selectedTab
export function sortSequences(
    selectedTab: string,
    sequences: Array<
        FlattenedDripSequence | EmailSequenceFragment | EmailAutomationStatusesFragment
    >
): Array<FlattenedDripSequence | EmailSequenceFragment | EmailAutomationStatusesFragment> {
    return sequences.sort((a, b) => {
        let timeA;
        let timeB;

        if (selectedTab === 'active') {
            timeA = getNextSendTime(a);
            timeB = getNextSendTime(b);
        } else if (selectedTab === 'completed') {
            timeA = getFinalSendTime(a);
            timeB = getFinalSendTime(b);
        }

        if (timeA && timeB) {
            return timeA - timeB;
        }

        if (timeA && !timeB) {
            return -1;
        }
        if (!timeA && timeB) {
            return 1;
        }

        return 0;
    });
}
